import { useEffect, useState, useSyncExternalStore } from 'react';
import { getConsent } from '@bookkeeper/shared/consent';
import { HOME_CTA_COPY } from '@/content/home-cta/copy';
import { CONSENT_CHANGED_EVENT } from '@/hooks/useCookieConsent';
import { useHydrated } from '@/hooks/useHydrated';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useTranslation } from '@/i18n/i18n';
import { trackLandingCtaClicked } from '@/lib/analytics';

const HERO_CTA_ID = 'hero-primary-cta';
const PRICING_ID = 'pricing';
const DISMISSED_KEY = 'bookeper_home_sticky_dismissed';
const DISMISSED_EVENT = 'bookeper:home-sticky-dismissed';

function subscribeToConsent(onChange: () => void) {
  window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
}
/** The consent banner is open exactly while no choice is stored. */
function consentBannerOpen() {
  return getConsent() === null;
}

/**
 * Stretches the viewport far below the fold, so an element still below the fold intersects and
 * only an element scrolled up past the top does not. A jump straight from below to above (fling,
 * anchor link) still crosses that edge and reports.
 */
const SCROLLED_PAST_ROOT_MARGIN = '0px 0px 100000px 0px';

/** Tracks whether the element with `id` intersects the root; `null` until the observer has reported. */
function useElementVisible(id: string, enabled: boolean, rootMargin?: string): boolean | null {
  const [visible, setVisible] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const element = document.getElementById(id);
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const last = entries[entries.length - 1];
        if (last) setVisible(last.isIntersecting);
      },
      { rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [id, enabled, rootMargin]);
  return visible;
}

let dismissedInMemory = false;
function subscribeToDismissal(onChange: () => void) {
  window.addEventListener(DISMISSED_EVENT, onChange);
  return () => window.removeEventListener(DISMISSED_EVENT, onChange);
}
function readDismissed(): boolean {
  if (dismissedInMemory) return true;
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}
function dismiss() {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    dismissedInMemory = true; // storage blocked: dismissed for this page view only
  }
  window.dispatchEvent(new Event(DISMISSED_EVENT));
}

/**
 * Mobile-only (<640 px) signup bar for the home page (spec 01 §2.6, slot `sticky_mobile`).
 * Shown once the hero button has scrolled up out of view (not while it is still below the fold
 * on a short screen); hidden while the hero button or the pricing section is on screen, and
 * while the consent banner is open, so it never covers one.
 * The dismiss `×` is remembered for the session. Rendered on the home page only.
 */
export const StickyMobileCta = () => {
  const { locale, t } = useTranslation();
  const copy = HOME_CTA_COPY[locale]?.sticky;
  const hydrated = useHydrated();
  const signupUrl = useSignupUrl({
    entityType: 'jdg',
    source: 'sticky_mobile',
    placement: 'sticky_mobile',
    page: 'home',
  });
  // false = scrolled up past the top; a hero still below the fold on a short screen is not "past"
  const heroBelowTop = useElementVisible(
    HERO_CTA_ID,
    hydrated && Boolean(copy),
    SCROLLED_PAST_ROOT_MARGIN
  );
  const pricingVisible = useElementVisible(PRICING_ID, hydrated && Boolean(copy));
  const bannerOpen = useSyncExternalStore(subscribeToConsent, consentBannerOpen, () => true);
  const dismissed = useSyncExternalStore(subscribeToDismissal, readDismissed, () => false);

  if (!copy) return null;

  const show =
    // Both observers must have reported: on a scroll restore at #pricing the hero can report
    // before pricing does, and an unknown pricing state must not let the bar cover it.
    hydrated && !dismissed && !bannerOpen && heroBelowTop === false && pricingVisible === false;
  const label = t('hero.ctaPrimary');

  return (
    <>
      {/* Reserves the bar's height at the end of the page so the footer is never covered. */}
      <div aria-hidden="true" className="h-16 sm:hidden" />
      {show && (
        <div
          data-testid="sticky-mobile-cta"
          className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t bg-background/95 px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-lg backdrop-blur-sm sm:hidden"
        >
          <a
            href={signupUrl}
            data-testid="sticky-mobile-cta-button"
            onClick={() =>
              trackLandingCtaClicked('sticky_mobile', label, {
                source: 'sticky_mobile',
                placement: 'sticky_mobile',
                page: 'home',
                locale,
                entity_type: 'jdg',
              })
            }
            className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground shadow-md shadow-primary/25"
          >
            {label}
          </a>
          <a
            href="#pricing"
            className="inline-flex h-11 items-center px-2 text-sm underline underline-offset-4"
          >
            {copy.pricing}
          </a>
          <button
            type="button"
            aria-label={copy.dismiss}
            onClick={dismiss}
            className="inline-flex size-11 items-center justify-center text-xl leading-none text-muted-foreground"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};
